import React, { useState, useEffect, useContext } from 'react';
import parse from 'html-react-parser';
import classNames from 'classnames';
import useVisibleProducts from '../../../hooks/useVisibleProducts';
import useElevarEvent from '../../../hooks/useElevarEvent';
import Hero from '../../Hero';
import ProductListing from '../../ProductListing';
import FullWidthBanner from '../../FullWidthBanner';
import PLPCategoryGroup from '../../PLP/CategoryGroup';
import PDPVideo from '../../PLP/Video';
import Faqs from '../../Faqs';
import { convertToElevarDataItem, getProductsById } from '../../../utils';
import { AuthContext } from '../../../context/auth-context';

const ns = `version-default-plp`;

const VersionDefault = ({
	category,
	location,
	productsVisible,
	promoCard,
	campaignCopy,
}) => {
	const rootClassnames = classNames({
		[`${ns}`]: true,
	});
	const {
		products,
		image,
		title,
		descriptionHtml,
		// #########################
    // # Collection METAFIELDS #
    // #########################
		collectionIconSvg,
		heroTitle,
		heroDescription,
		heroBullets,
		heroEnableWave,
		heroFontIsDark,
		heroImageSourceUrlMobile,
		heroImageSourceUrlTablet,
		heroImageSourceUrlDesktop,
		productListingTitle,
		productListingDescription,
		activeCampaign,
		fullWidthBannerTitle,
		fullWidthBannerDescription,
		fullWidthBannerLinkLabel,
		fullWidthBannerLinkUrl,
		fullWidthBannerImageAltText,
		fullWidthBannerImageSourceUrl,
		fullWidthBannerFontIsDark,
		videoContentSectionHeadline,
		videoContentSectionUrl,
		copyOne,
		productsOne,
		bannerImageSourceUrlDesktop,
		bannerImageSourceUrlTablet,
		bannerImageSourceUrlMobile,
		faqs,
	} = category;
	const [currentProducts, setCurrentProducts] = useState([]);
	const hasProductCarouselFromMetafields = copyOne && productsOne;

	const { state: authState } = useContext(AuthContext);
	const { userProperties } = authState;

	useEffect(() => {
		const fetchProducts = async() => {
			try {
				const productsById = await getProductsById(JSON.parse(productsOne?.value));
				const extractedProducts = (productsById || []).map(p => p.product);
				setCurrentProducts(extractedProducts);
			} catch (error) {
				console.error('Error fetching data:', error);
			}
		};
		fetchProducts();
	}, [products, productsOne]);

	const heroDetails = {
		title: heroTitle?.value,
		description: heroDescription?.value,
		image: {
			altText: image?.altText || 'Category Image',
			title: null,
			sourceUrl: image?.originalSrc,
			mainImageMobile: { sourceUrl: heroImageSourceUrlMobile?.value },
			tabletImage: { sourceUrl: heroImageSourceUrlTablet?.value },
			desktopImage: { sourceUrl: heroImageSourceUrlDesktop?.value },
		},
	};

	const {
		impressions,
		handleLoadMore,
		handleFilteredProducts,
  } = useVisibleProducts(products, location);

	const initialVisibleAmount = 8;
	const initialProducts = products.map((product, index) => convertToElevarDataItem({ item: product, index, location }));
	const initialDisplayedProducts = initialProducts.slice(0, initialVisibleAmount);
	const allImpressions = [ ...initialDisplayedProducts, ...impressions ];

	useElevarEvent({
		location,
		productLength: impressions.length,
		eventType: 'dl_view_item_list',
		allImpressions,
		impressions,
		userProperties
	});

	return (
		<div className={rootClassnames}>
			<Hero
				pageType="category"
				heroTitle={title}
				heroDescription={descriptionHtml}
				bullets={heroBullets ? JSON.parse(heroBullets.value) : null}
				heroEnableWave={heroEnableWave?.value}
				heroFontIsDark={heroFontIsDark}
				heroDetails={heroDetails}
				icon={collectionIconSvg?.value}
				isH1={false}
			/>
			<ProductListing
				title={productListingTitle?.value}
				description={productListingDescription?.value}
				location={location}
				category={category}
				productsVisible={productsVisible}
				initialVisibleAmount={initialVisibleAmount}
				products={products}
				promo={promoCard}
				activeCampaign={activeCampaign}
				campaignCopy={campaignCopy}
				onLoadMore={handleLoadMore}
				isH1
			/>
			{(fullWidthBannerTitle?.value &&
				fullWidthBannerDescription?.value) ? (
				<FullWidthBanner
					title={fullWidthBannerTitle?.value}
					description={fullWidthBannerDescription?.value}
					link={{
						label: fullWidthBannerLinkLabel?.value,
						url: fullWidthBannerLinkUrl?.value,
					}}
					image={{
						altText: fullWidthBannerImageAltText?.value || fullWidthBannerTitle?.value,
						sourceUrl: fullWidthBannerImageSourceUrl?.value,
						mainImageMobile: {
							sourceUrl: bannerImageSourceUrlMobile?.value,
						},
						tabletImage: {
							sourceUrl: bannerImageSourceUrlTablet?.value,
						},
						laptopImage: {
							sourceUrl: bannerImageSourceUrlDesktop?.value,
						},
						desktopImage: {
							sourceUrl: bannerImageSourceUrlDesktop?.value,
						},
					}}
					fullWidthBannerFontIsDark={fullWidthBannerFontIsDark?.value}
				/>
			) : null}
			<PDPVideo
				videoContentSectionHeadline={videoContentSectionHeadline}
				videoContentSectionUrl={videoContentSectionUrl}
			/>
			{faqs !== null && <Faqs faqItems={faqs} />}
			{hasProductCarouselFromMetafields && (
				<PLPCategoryGroup
					location={location}
					title={copyOne?.value?.split('|')[0]}
					products={currentProducts}
					headline={copyOne?.value?.split('|')[1]}
					onFilteredProducts={handleFilteredProducts}
				/>
			)}
		</div>
	);
};

export default VersionDefault;
